/**
 * Format a number with appropriate units (K, M, B)
 */
export function formatNumber(num) {
    if (num >= 1000000000) {
        return (num / 1000000000).toFixed(1) + 'B';
    }
    if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
}
/**
 * Format duration in minutes to human readable format
 */
export function formatDuration(minutes) {
    if (minutes < 60) {
        return `${minutes}min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
        return `${hours}h`;
    }
    return `${hours}h ${remainingMinutes}min`;
}
/**
 * Format temperature with unit
 */
export function formatTemperature(celsius, unit = 'C') {
    if (unit === 'F') {
        const fahrenheit = (celsius * 9) / 5 + 32;
        return `${Math.round(fahrenheit)}°F`;
    }
    return `${celsius}°C`;
}
/**
 * Format coffee to water ratio
 */
export function formatRatio(ratio) {
    return `1:${ratio}`;
}
/**
 * Format date to relative time (e.g., "2 hours ago")
 */
export function formatRelativeTime(date) {
    const now = new Date();
    const targetDate = typeof date === 'string' ? new Date(date) : date;
    const diffInSeconds = Math.floor((now.getTime() - targetDate.getTime()) / 1000);
    if (diffInSeconds < 60) {
        return 'just now';
    }
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
        return `${diffInMinutes}m ago`;
    }
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
        return `${diffInHours}h ago`;
    }
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) {
        return `${diffInDays}d ago`;
    }
    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) {
        return `${diffInWeeks}w ago`;
    }
    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
        return `${diffInMonths}mo ago`;
    }
    const diffInYears = Math.floor(diffInDays / 365);
    return `${diffInYears}y ago`;
}
/**
 * Format date to readable format
 */
export function formatDate(date, options) {
    const targetDate = typeof date === 'string' ? new Date(date) : date;
    const defaultOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    };
    return targetDate.toLocaleDateString('en-US', options || defaultOptions);
}
/**
 * Format currency amount
 */
export function formatCurrency(amount, currency = 'USD') {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
    }).format(amount);
}
/**
 * Format rating with stars
 */
export function formatRating(rating) {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    return ('★'.repeat(fullStars) +
        (hasHalfStar ? '☆' : '') +
        '☆'.repeat(emptyStars) +
        ` (${rating.toFixed(1)})`);
}
/**
 * Truncate text with ellipsis
 */
export function truncateText(text, maxLength) {
    if (text.length <= maxLength) {
        return text;
    }
    return text.slice(0, maxLength - 3) + '...';
}
/**
 * Format file size in human readable format
 */
export function formatFileSize(bytes) {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0)
        return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
}
/**
 * Format username with @ prefix
 */
export function formatUsername(username) {
    return `@${username}`;
}
/**
 * Format display name or fallback to username
 */
export function formatDisplayName(displayName, username) {
    return displayName || username || 'Unknown User';
}
